import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Vehicle } from '@otw/models'
import { capResults, SEARCH_RESULT_CAP } from 'hooks/search/filterSearchItems'
import { fetchMasterInventory, SEARCH_CACHE_STALE_MS } from 'hooks/search/searchCaches'

interface UseVehicleSearchOptions {
  searchTerm: string
  inventoryCollection: 'master_inventory' | 'shield_inventory'
  enabled?: boolean
}

export function filterVehiclesByTerm(vehicles: Vehicle[], searchTerm: string): Vehicle[] {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []
  }

  const trimmedTerm = searchTerm.trim().toUpperCase()
  const matchingVehicles = vehicles.filter((vehicle) => {
    const vin = vehicle.vin?.toUpperCase() || ''
    const stockNumber = vehicle.stock_number?.toUpperCase() || ''
    return vin.includes(trimmedTerm) || stockNumber.includes(trimmedTerm)
  })

  return capResults(matchingVehicles, SEARCH_RESULT_CAP)
}

export const useVehicleSearch = ({
  searchTerm,
  inventoryCollection,
  enabled = true,
}: UseVehicleSearchOptions) => {
  const { data: vehicles = [], isPending } = useQuery({
    queryKey: [inventoryCollection],
    queryFn: fetchMasterInventory,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const data = useMemo(
    () => filterVehiclesByTerm(vehicles, searchTerm),
    [vehicles, searchTerm],
  )

  return {
    data,
    isLoading: isPending && vehicles.length === 0,
  }
}
